import React, { useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Routes, Route, useSearchParams, Outlet } from 'react-router-dom';

import FujiModal from './components/fuji-modal/FujiModal';
import Splash from './components/splash/Splash';
import ErrorPage from './components/error-page/ErrorPage';
import TimeoutPage from './components/error-page/TimeoutPage';

import { RequireAuth } from './auth/RequireAuth';
import { useFujiAuth } from './auth/useFujiAuth';
import MainContainer from './auth/MainContainer';
import AuthenContainer from './auth/AuthenContainer';

/** 「買う」タブ以下 */
import BuyTicketListScreen from './screens/buy/BuyTicketListScreen/BuyTicketListScreen';
import BuyTicketInfoScreen from './screens/buy/BuyTicketInfoScreen/BuyTicketInfoScreen';
import BuyTicketSuccessScreen from './screens/buy/BuyTicketSuccessScreen/BuyTicketSuccessScreen';
import ReservationTicketInfoScreen from './screens/buy/ReservationTicketInfoScreen/ReservationTicketInfoScreen';
import BuyGroupTicketScreen from './screens/buy/BuyGroupTicketScreen/BuyGroupTicketScreen';
import BuyCreditCardPaymentScreen from './screens/buy/BuyCreditCardPaymentScreen/BuyCreditCardPaymentScreen';
import BuyCreditCardCertificateScreen from './screens/buy/BuyCreditCardCertificateScreen/BuyCreditCardCertificateScreen';

/** 「使う」タブ以下 */
import BoughtTicketDetailScreen from './screens/bought/BoughtTicketDetailScreen/BoughtTicketDetailScreen';
import BoughtTicketRefundScreen from './screens/bought/BoughtTicketRefundScreen/BoughtTicketRefundScreen';
import BoughtTicketListScreen from './screens/bought/BoughtTicketListScreen/BoughtTicketListScreen';
import TicketDisplayScreen from './screens/bought/TicketDisplayScreen/TicketDisplayScreen';
import UsedTicketListScreen from './screens/bought/UsedTicketListScreen/UsedTicketListScreen';
import CameraCodeScreen from './screens/bought/CameraCodeScreen/CameraCodeScreen';
import SpotNumberSelectionScreen from './screens/bought/SpotNumberSelectionScreen/SpotNumberSelectionScreen';
import SpotResultScreen from './screens/bought/SpotResultScreen/SpotResultScreen';
import BoughtTicketDistributionScreen from './screens/bought/BoughtTicketDistributionScreen/BoughtTicketDistributionScreen';
import BoughtDistributionsInfoScreen from './screens/bought/BoughtDistributionsInfoScreen/BoughtDistributionsInfoScreen';

/** 「マイページ」タブ以下 */
import ChangeEmailConfirmScreen from './screens/myProfile/ChangeEmailConfirmScreen/ChangeEmailConfirmScreen';
import ChangeEmailScreen from './screens/myProfile/ChangeEmailScreen/ChangeEmailScreen';
import ChangePasswordScreen from './screens/myProfile/ChangePasswordScreen/ChangePasswordScreen';
import ContactScreen from './screens/myProfile/ContactScreen/ContactScreen';
import DeleteUserScreen from './screens/myProfile/DeleteUserScreen/DeleteUserScreen';
import EmailLoginScreen from './screens/myProfile/EmailLoginScreen/EmailLoginScreen';
import LanguageScreen from './screens/myProfile/LanguageScreen/LanguageScreen';
import LicenseContentScreen from './screens/myProfile/LicenseContentScreen/LicenseContentScreen';
import LicenseListScreen from './screens/myProfile/LicenseListScreen/LicenseListScreen';
import LoginScreen from './screens/myProfile/LoginScreen/LoginScreen';
import MyProfileScreen from './screens/myProfile/MyProfileScreen/MyProfileScreen';
import RegisterEmailConfirmScreen from './screens/myProfile/RegisterEmailConfirmScreen/RegisterEmailConfirmScreen';
import RegisterEmailScreen from './screens/myProfile/RegisterEmailScreen/RegisterEmailScreen';
import ResetPasswordEmailInputScreen from './screens/myProfile/ResetPasswordEmailInputScreen/ResetPasswordEmailInputScreen';
import ResetPasswordSubmitScreen from './screens/myProfile/ResetPasswordSubmitScreen/ResetPasswordSubmitScreen';
import UserInfoScreen from './screens/myProfile/UserInfoScreen/UserInfoScreen';
import ReservationUserInfoScreen from './screens/buy/ReservationUserInfoScreen/ReservationUserInfoScreen';
import ReservationCalendarScreen from './screens/buy/ReservationCalendarScreen/ReservationCalendarScreen';
import ReservationTicketConfirmScreen from './screens/buy/ReservationTicketConfirmScreen/ReservationTicketConfirmScreen';
import ReservationSectionScreen from './screens/buy/ReservationSectionScreen/ReservationSectionScreen';
import BuyVoucherScreen from './screens/buy/BuyVoucherScreen/BuyVoucherScreen';

/** 外部からアクセス */
import ReceiveScreen from './screens/ReceiveScreen/ReceiveScreen';
import ReceiveLinkRelayScreen from './screens/ReceiveLinkRelayScreen/ReceiveLinkRelayScreen';
import ReceiveEditReservationScreen from './screens/ReceiveScreen/ReceiveEditReservationScreen';
import ReceiveRefundScreen from './screens/ReceiveScreen/ReceiveRefundScreen';

import './App.css';
import i18n, { getSelectableLanguages } from './assets/languages/configs';
import useAppConfig from './hooks/useAppConfig';
import useLocalizedTime from './hooks/useLocalizedTime';
import CenterLoading from './components/CenterLoading/CenterLoading';
import { applyFontFromLanguage } from './utils';
import { GMO_PG_URL } from './utils/Constants';
import WebQrDisplayScreen from './screens/WebQrDisplayScreen/WebQrDisplayScreen';
import { RootState } from 'redux/reducers/rootReducer';

declare global {
    interface Window {
        Multipayment?: any;
        ApplePaySession?: any;
        google?: any;
    }
}

const App = () => {
    const isServiceWorkerUpdated = useSelector((state: RootState) => state.serviceWorkerUpdated);
    const serviceWorkerRegistration = useSelector((state: RootState) => state.serviceWorkerRegistration);
    const { isMaintenance, endAt: maintenanceEndAt } = useSelector((state: RootState) => state.serverMaintenance);
    const { t } = useTranslation();
    const { isLoading } = useFujiAuth();
    const { isLoadingApp, shouldShowApp, errorType, setFillHeight, configApp } = useAppConfig();
    const [searchParams] = useSearchParams();
    const { language } = i18n;
    const dispatch = useDispatch();
    const { formatTime } = useLocalizedTime();

    const updateServiceWorker = () => {
        if (serviceWorkerRegistration) {
            void serviceWorkerRegistration(true);
        }
    };

    useLayoutEffect(() => {
        // アプリ設定を行う。
        void configApp();

        // 画面のサイズ変動があった時に高さを再計算する
        window.addEventListener('resize', setFillHeight);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', setFillHeight);
        }
        // 画面の向きが変わった時に高さを再計算する。
        window.addEventListener('orientationchange', setFillHeight);
        // 初期化
        setFillHeight();
        if (!window.Multipayment) {
            const multiPaymentScript = GMO_PG_URL;
            const existingScript = document.querySelector(`script[src="${multiPaymentScript}"]`);
            if (existingScript) {
                existingScript.remove();
            }
            const script = document.createElement('script');
            script.src = multiPaymentScript;
            script.async = true;

            document.head.appendChild(script);
        }
        window.addEventListener('online', () => {
            if (!window.Multipayment) {
                const multiPaymentScript = GMO_PG_URL;
                const existingScript = document.querySelector(`script[src="${multiPaymentScript}"]`);
                if (existingScript) {
                    existingScript.remove();
                }
                const script = document.createElement('script');
                script.src = multiPaymentScript;
                script.async = true;

                document.head.appendChild(script);
            }
            if (!window.ApplePaySession) {
                const applePaySessionScript = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
                const existingScript = document.querySelector(`script[src="${applePaySessionScript}"]`);
                if (existingScript) {
                    existingScript.remove();
                }
                const script = document.createElement('script');
                script.src = applePaySessionScript;
                script.async = true;

                document.head.appendChild(script);
            }
            if (!window.google) {
                const googleScript = 'https://pay.google.com/gp/p/js/pay.js';
                const existingScript = document.querySelector(`script[src="${googleScript}"]`);
                if (existingScript) {
                    existingScript.remove();
                }
                const script = document.createElement('script');
                script.src = googleScript;
                script.async = true;

                document.head.appendChild(script);
            }
        });
    }, [configApp, setFillHeight]);

    useEffect(() => {
        if (isLoadingApp) {
            // アプリ設定の取得中は何もしない
            // アプリ設定取得後に、URLのクエリパラメータやlocalStorageから言語設定を行う。
            return;
        }
        if (searchParams.get('language')) {
            // i18nの言語指定に合わせる
            let language = searchParams.get('language');
            // 中国語の言語指定を調整
            language = (() => {
                if (!language) return language;
                language = language.replace(/-/g, '_');
                if (['zh', 'zh_cn', 'zh_CN'].includes(language)) {
                    return 'zh';
                }
                if (['zh_tw', 'zh_TW'].includes(language)) {
                    return 'zh-TW';
                }
                return language;
            })();

            // アプリ設定から、選択可能な言語一覧を取得する
            const selectableLanguages = getSelectableLanguages();
            if (!selectableLanguages.some(({ key }) => key === language)) {
                // 選択可能でない場合は英語で表示する
                language = 'en';
            }
            localStorage.setItem('lang', language ?? 'en');
            void i18n.changeLanguage(language ?? 'en');
        }
        return () => {};
    }, [searchParams, isLoadingApp]);

    applyFontFromLanguage(language);

    if (isLoadingApp || isLoading) {
        // 認証の確認中
        return <CenterLoading overFooter />;
    }
    if (!shouldShowApp) {
        // アプリ設定の取得に失敗
        if (errorType === 'timeout') {
            // タイムアウトエラーユーザー通信環境責任
            return (
                <Routes>
                    <Route path="*" element={<TimeoutPage />} />
                </Routes>
            );
        } else {
            // その他のエラー
            return <span>{'アプリ設定の取得に失敗しました。'}</span>;
        }
    }

    return (
        <div className="app-container">
            <FujiModal
                body={t('update.title')}
                txtSubmit={t('update.btn-title')}
                isOpen={isServiceWorkerUpdated}
                onSubmit={updateServiceWorker}
            />
            <FujiModal
                body={t('notification.server-maintenance-title', { endAt: formatTime(maintenanceEndAt, 'lll') })}
                txtSubmit={t('modal.warning-confirm')}
                isOpen={isMaintenance}
                onSubmit={() => {
                    dispatch({
                        type: 'SERVER_MAINTENANCE',
                        payload: {
                            isMaintenance: false,
                            endAt: null,
                        },
                    });
                }}
            />
            <Routes>
                <Route index element={<Splash />} />
                <Route path="/" element={<MainContainer />}>
                    <Route path="buy">
                        <Route index element={<BuyTicketListScreen />} />
                        <Route path="ticket">
                            <Route index element={<BuyTicketInfoScreen />} />
                            <Route
                                element={
                                    <RequireAuth>
                                        <Outlet />
                                    </RequireAuth>
                                }
                            >
                                <Route path="credit-payment" element={<BuyCreditCardPaymentScreen />} />
                                <Route path="credit-certificate" element={<BuyCreditCardCertificateScreen />} />
                                <Route path="success" element={<BuyTicketSuccessScreen />} />
                            </Route>
                        </Route>
                        <Route path="group-ticket" element={<BuyGroupTicketScreen />} />
                        <Route path="reservation">
                            <Route index element={<ReservationTicketInfoScreen />} />
                            <Route path="user-info" element={<ReservationUserInfoScreen />} />
                            <Route path="calendar" element={<ReservationCalendarScreen />} />
                            <Route path="confirm" element={<ReservationTicketConfirmScreen />} />
                            <Route path="section" element={<ReservationSectionScreen />} />
                            <Route
                                element={
                                    <RequireAuth>
                                        <Outlet />
                                    </RequireAuth>
                                }
                            >
                                <Route path="credit-payment" element={<BuyCreditCardPaymentScreen />} />
                                <Route path="credit-certificate" element={<BuyCreditCardCertificateScreen />} />
                                <Route path="success" element={<BuyTicketSuccessScreen />} />
                            </Route>
                        </Route>
                        <Route path="vouchers" element={<BuyVoucherScreen />} />
                    </Route>
                    <Route path="bought">
                        <Route index element={<BoughtTicketListScreen />} />
                        <Route path="used" element={<UsedTicketListScreen />} />
                        <Route path="detail" element={<BoughtTicketDetailScreen />} />
                        <Route path="refund" element={<BoughtTicketRefundScreen />} />
                        <Route path="ticket" element={<TicketDisplayScreen />} />
                        <Route path="code" element={<CameraCodeScreen />} />
                        <Route path="distributions">
                            <Route index element={<BoughtTicketDistributionScreen />} />
                            <Route index path="info" element={<BoughtDistributionsInfoScreen />} />
                        </Route>
                        <Route
                            element={
                                <RequireAuth>
                                    <Outlet />
                                </RequireAuth>
                            }
                        >
                            <Route path="spot-number" element={<SpotNumberSelectionScreen />} />
                            <Route path="spot-result" element={<SpotResultScreen />} />
                        </Route>
                    </Route>

                    <Route path="profile">
                        <Route index element={<MyProfileScreen />} />
                        <Route
                            path="info"
                            element={
                                <RequireAuth>
                                    <Outlet />
                                </RequireAuth>
                            }
                        >
                            <Route index element={<UserInfoScreen />} />
                            <Route path="change-password" element={<ChangePasswordScreen />} />
                            <Route path="change-mail" element={<ChangeEmailScreen />} />
                            <Route path="confirm" element={<ChangeEmailConfirmScreen />} />
                            <Route path="delete" element={<DeleteUserScreen />} />
                        </Route>
                        <Route path="language" element={<LanguageScreen />} />
                        <Route path="contact" element={<ContactScreen />} />
                        <Route path="licenses" element={<LicenseListScreen />} />
                        <Route path="licenses-content" element={<LicenseContentScreen />} />
                    </Route>
                    <Route path="receive">
                        <Route index element={<ReceiveScreen />} />
                        <Route path="edit-reservation" element={<ReceiveEditReservationScreen />} />
                        <Route path="refund" element={<ReceiveRefundScreen />} />
                    </Route>
                    <Route path="receive-link-relay">
                        <Route index element={<ReceiveLinkRelayScreen />} />
                    </Route>
                    <Route path="web-qr-display">
                        <Route index element={<WebQrDisplayScreen />} />
                    </Route>
                </Route>
                <Route path="/auth" element={<AuthenContainer />}>
                    <Route index element={<LoginScreen />} />
                    <Route path="email" element={<EmailLoginScreen />} />
                    <Route path="register" element={<RegisterEmailScreen />} />
                    <Route path="confirm" element={<RegisterEmailConfirmScreen />} />
                    <Route path="forgot-password" element={<ResetPasswordEmailInputScreen />} />
                    <Route path="new-password" element={<ResetPasswordSubmitScreen />} />
                </Route>
                <Route path="*" element={<ErrorPage />} />
            </Routes>
        </div>
    );
};

export default App;
